import { useDoubleClickProtection } from '@web_opxp/components';
import { AuthSelectors, FirebaseActions, useTranslation } from '@web_opxp/features';
import { FC, MouseEventHandler, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { buildManagementUrl, redirectToManagement } from '../base/historyService';
import { Banner } from '../components/Banner';
import { IState } from '../store/state';
import { SubscriptionSelectors } from '../subscription';
import { canShowBanner, saveBannerTimestamp } from './utils';

export interface IRestoreBannerProps {
  session?: string;
  show: boolean;
  userId?: number;
  onClick: () => void;
}

export const mapStateToProps = (state: IState) => {
  const show = SubscriptionSelectors.hasCancelledWebSubscriptions(state);
  const session = AuthSelectors.getToken(state);
  const userId = AuthSelectors.getUserId(state);
  return { show, session, userId };
};

export const mapDispatchToProps = {
  onClick: () => FirebaseActions.analyticsEventAction({ eventName: 'd_restore' }),
};

export const RestoreBannerFC: FC<IRestoreBannerProps> = ({ session, show, userId, onClick }) => {
  const { language, t } = useTranslation();

  // control visibility of the banner using timestamp in local storage
  const [canShow, setCanShow] = useState(false);
  useEffect(() => {
    if (!show) {
      // hide banner if main conditions are not met
      setCanShow(false);
      return;
    }
    const timestampCheck = canShowBanner(userId);
    setCanShow(timestampCheck);
  }, [show, userId]);

  // set link to management page using current language and session token
  const [restoreLink, setRestoreLink] = useState('');
  useEffect(() => {
    // update link if token or language changes
    setRestoreLink(buildManagementUrl(language, session));
  }, [language, session]);

  const closeHandler = useCallback(() => {
    // save last timestamp of the close event
    saveBannerTimestamp(userId);
  }, [userId]);

  const clickHandler: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault();
      onClick();
      // redirect manually after delay to ensure that event was tracked
      setTimeout(() => {
        redirectToManagement(language, session);
      }, 500);
    },
    [language, session, onClick]
  );

  const onClickHandler = useDoubleClickProtection(clickHandler);

  if (!canShow) {
    return null;
  }

  return (
    <Banner canClose={true} show={canShow} closeLabel={t('RestoreBanner.Close')} onClose={closeHandler} maxHeight={64}>
      <a href={restoreLink} className="restore-banner-content" onClick={onClickHandler}>
        {t('RestoreBanner.Label')}
      </a>
    </Banner>
  );
};

const RestoreBanner = connect(mapStateToProps, mapDispatchToProps)(RestoreBannerFC);
export default RestoreBanner;
